import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { CustomerList, ListDetailsProps } from 'shared-types'
import {
  CustomerListActionType,
  CustomerListActions,
  useSession,
  useToast,
} from '~/hooks'
import { Client } from '~/customClients/client'

export const useCustomerListDetail = (listDetail?: CustomerList) => {
  const { session } = useSession()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { showNotification, closeNotification } = useToast()
  const [detail, setDetail] = useState<ListDetailsProps>()
  const [updatedAt, setUpdatedAt] = useState(Date.now())
  const [isOpenListNameModal, setIsOpenListNameModal] = useState(false)

  const fetchCustomerListDetail = useCallback(
    async (listId: string, setLoading?: (arg) => void) => {
      if (!session) {
        setLoading?.(false)
        return false
      }

      const res = await Client.lists.getListDetail(listId, session)
      if (res.getErrorSafe()) {
        setLoading?.(false)
        return false
      }

      setDetail(res.getValueSafe())
      setUpdatedAt(Date.now())
      setLoading?.(false)
      return true
    },
    [session, setDetail, setUpdatedAt]
  )

  const addToCustomerList = useCallback(
    async (listId: string, productIds: string[]) => {
      if (!session) {
        return false
      }

      const res = await Client.lists.addToList(listId, productIds, session)
      if (res.getErrorSafe()) {
        return false
      }

      fetchCustomerListDetail(listId)
      return true
    },
    [session, fetchCustomerListDetail]
  )

  const removeFromCustomerList = useCallback(
    async (listId: string, productIds: string[]) => {
      if (!session) {
        return false
      }

      const res = await Client.lists.removeToList(listId, productIds, session)
      if (res.getErrorSafe()) {
        return false
      }

      fetchCustomerListDetail(listId)
      return true
    },
    [session, fetchCustomerListDetail]
  )

  const updateCustomerList = useCallback(
    async (listId: string, productId: string, quantity: number) => {
      const res = await Client.lists.updateList(
        listId,
        session,
        productId,
        quantity
      )
      if (res.getErrorSafe()) {
        return false
      }

      setDetail(res.getValueSafe())
      setUpdatedAt(Date.now())
      return true
    },
    [session, setDetail, setUpdatedAt]
  )

  const duplicateCustomerList = useCallback(
    async (listId: string) => {
      if (!session) {
        return false
      }

      const res = await Client.lists.duplicateList(listId, session)
      if (res.getErrorSafe()) {
        return false
      }

      const lists = res.getValueSafe()
      if (!lists?.length || !detail?.name) {
        return false
      }

      showNotification({
        id: 'duplicated-list',
        children: 'A List was duplicated.',
        onClose: closeNotification,
      })
      const duplicated = lists.find((li) => {
        const offset = detail.name.length
        const name = li.name.slice(0, offset)
        const append = li.name.slice(offset)
        return name === detail.name && /^\([0-9]+\)$/.test(append.trim())
      })

      if (duplicated) {
        router.push(`/lists/${duplicated.id}`)
      }

      return true
    },
    [session, detail?.name, showNotification, closeNotification, router]
  )

  const renameCustomerList = useCallback(
    async (listId: string, name: string) => {
      if (!session) {
        return false
      }

      const res = await Client.lists.renameList(listId, name, session)
      if (res.getErrorSafe()) {
        return false
      }

      await fetchCustomerListDetail(listId)
      return true
    },
    [session, fetchCustomerListDetail]
  )

  const deleteCustomerList = useCallback(
    async (listId: string) => {
      if (!session) {
        return false
      }

      const res = await Client.lists.deleteList(listId, session)
      return !res.getErrorSafe()
    },
    [session]
  )

  const addCustomerListToCart = useCallback(
    async (listId: string) => {
      if (!session) {
        return false
      }

      const res = await Client.lists.addToCart(listId, session)
      if (res.getErrorSafe()) {
        return false
      }

      queryClient.setQueryData(['cart', session], res.getValueSafe())
      fetchCustomerListDetail(listId)
      return true
    },
    [session, queryClient, fetchCustomerListDetail]
  )

  const handleCustomerListAction = useCallback(
    async (listId: string, action: CustomerListActionType) => {
      switch (action) {
        case CustomerListActions.RENAME: {
          setIsOpenListNameModal(true)
          break
        }
        case CustomerListActions.DUPLICATE_LIST: {
          await duplicateCustomerList(listId)
          break
        }
        case CustomerListActions.ADD_ITEMS_TO_CART: {
          await addCustomerListToCart(listId)
          break
        }
        case CustomerListActions.DELETE_LIST: {
          await deleteCustomerList(listId)
          break
        }
        default:
      }
    },
    [
      setIsOpenListNameModal,
      duplicateCustomerList,
      addCustomerListToCart,
      deleteCustomerList,
    ]
  )

  useEffect(() => {
    setDetail(listDetail)
  }, [listDetail])

  return useMemo(() => {
    return {
      detail,
      isOpenListNameModal,
      updatedAt,
      renameCustomerList,
      duplicateCustomerList,
      deleteCustomerList,
      addToCustomerList,
      updateCustomerList,
      removeFromCustomerList,
      addCustomerListToCart,
      handleCustomerListAction,
      toggleListNameModal: setIsOpenListNameModal,
      fetchCustomerListDetail,
    }
  }, [
    detail,
    isOpenListNameModal,
    updatedAt,
    renameCustomerList,
    duplicateCustomerList,
    deleteCustomerList,
    addToCustomerList,
    updateCustomerList,
    removeFromCustomerList,
    addCustomerListToCart,
    handleCustomerListAction,
    setIsOpenListNameModal,
    fetchCustomerListDetail,
  ])
}
